import React, { useEffect, useState } from 'react';
import { Button, Logo } from '@whoop/web-components';
import CenteredLayout from 'layouts/CenteredLayout';
import consent from 'services/consent';
import {
  ConsentPageResponse,
  UserLimitReachedOrExceededError,
} from 'types/consent';
import ConsentItem from 'components/consent/ConsentItem';
import FormError from 'components/shared/FormError';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';

const useConsentPageData = (consentChallenge: string | null) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ConsentPageResponse | undefined>(undefined);

  const getPageData = async (challenge: string) => {
    try {
      setLoading(true);
      setData(
        await consent.getConsentPageData({ consentChallenge: challenge }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (consentChallenge) getPageData(consentChallenge);
  }, [consentChallenge]);

  return { consentPageData: data, loading };
};

function Consent() {
  const { t } = useTranslation('consent');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(undefined);
  const { consentPageData, loading } = useConsentPageData(
    searchParams.get('consent_challenge'),
  );

  const submit = async (accepted: boolean) => {
    try {
      setError(undefined);
      setSubmitting(true);
      const { redirectUri } = await consent.postConsent({
        consentChallenge: searchParams.get('consent_challenge') || '',
        accepted,
      });
      window.location.replace(redirectUri);
    } catch (e) {
      if (e instanceof UserLimitReachedOrExceededError) {
        setError(t('userLimitReachedOrExceeded'));
      } else {
        navigate('/expired', { replace: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const grantConsent = () => submit(true);

  const denyConsent = () => submit(false);

  return (
    <CenteredLayout>
      <h2 className={styles.title} data-testid="title">
        {consentPageData?.title}
      </h2>

      <div className={styles.logoSection}>
        <Logo
          className={styles.whoopLogo}
          variant="circle"
          height={60}
          width={60}
        />
        {consentPageData?.logo && (
          <>
            <img
              className={styles.rightArrow}
              src="/right_arrow.svg"
              alt={t('rightArrow')}
            />
            <img
              src={consentPageData?.logo.src}
              alt={consentPageData?.logo.alt}
              height={60}
              width={60}
              data-testid="client-logo"
            />
          </>
        )}
      </div>

      <p className={styles.body} data-testid="body">
        {consentPageData?.body}
      </p>
      <div className={styles.consentArea}>
        {consentPageData?.consentItems.map((i) => (
          <ConsentItem
            key={i.title}
            consentItem={i}
            data-testid="consent-item"
          />
        ))}
      </div>
      {consentPageData?.privacyPolicyText &&
        consentPageData?.privacyPolicyUrl && (
          <p
            className={styles.privacyPolicyWrapper}
            data-testid="privacy-policy"
          >
            <a
              className={styles.privacyPolicy}
              href={consentPageData?.privacyPolicyUrl}
              target="_blank"
              rel="noreferrer"
            >
              {consentPageData?.privacyPolicyText}
            </a>
          </p>
        )}
      {error && <FormError message={error} data-testid="form-error" />}
      <Button
        label={t('grantConsent')}
        variant="primary"
        type="submit"
        className={styles.button}
        onClick={() => grantConsent()}
        disabled={submitting || loading}
        data-testid="grant-consent-button"
      />
      <Button
        label={t('denyConsent')}
        variant="secondary"
        className={styles.button}
        onClick={() => denyConsent()}
        disabled={submitting || loading}
        data-testid="deny-consent-button"
      />
    </CenteredLayout>
  );
}

export default Consent;
